import { useState, useEffect, useCallback } from 'react';

export interface RecentCalculatedMap {
  id?: string;
  firstName: string;
  familyName?: string;
  motherName?: string;
  birthDate: string;
  birth_time?: string;
  birth_lat?: number;
  birth_lon?: number;
  city_name?: string;
  gender?: string;
  timezone?: string;
  sunLongitude?: number;
  astrology_cache?: unknown;
  soul_root?: string;
  calculatedAt: number;
}

const STORAGE_KEY = 'spirit_recent_calculated_maps';
const MAX_RECENT_MAPS = 10;
const SYNC_EVENT_NAME = 'spirit-recent-maps-updated';

export function normalizeString(str?: string): string {
  return (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function normalizeDate(dateStr?: string): string {
  if (!dateStr) return '';
  const clean = dateStr.trim().split('T')[0];
  // Handle DD/MM/YYYY or DD-MM-YYYY format
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(clean)) {
    const parts = clean.split(/[/-]/);
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  return clean;
}

export function areMapsEquivalent(a: Partial<RecentCalculatedMap>, b: Partial<RecentCalculatedMap>): boolean {
  const aFirst = normalizeString(a.firstName);
  const bFirst = normalizeString(b.firstName);
  const aFamily = normalizeString(a.familyName);
  const bFamily = normalizeString(b.familyName);
  const aDate = normalizeDate(a.birthDate);
  const bDate = normalizeDate(b.birthDate);

  // 1. Direct ID match ONLY if identity (name and birthDate) is not contradictory
  if (a.id && b.id && a.id === b.id) {
    if (aFirst && bFirst && aFirst !== bFirst) return false;
    if (aDate && bDate && aDate !== bDate) return false;
    return true;
  }

  // 2. Core identity match: normalized firstName + familyName + birthDate
  if (aFirst && bFirst && aFirst === bFirst && aFamily === bFamily && aDate && bDate && aDate === bDate) {
    return true;
  }

  return false;
}

export function mergeMapEntries(existing: RecentCalculatedMap, incoming: RecentCalculatedMap): RecentCalculatedMap {
  return {
    ...existing,
    ...incoming,
    // Keep ID if either existing or incoming has it
    id: incoming.id || existing.id,
    // Keep richer fields if missing in incoming
    motherName: incoming.motherName || existing.motherName,
    birth_time: incoming.birth_time || existing.birth_time,
    birth_lat: incoming.birth_lat !== undefined ? incoming.birth_lat : existing.birth_lat,
    birth_lon: incoming.birth_lon !== undefined ? incoming.birth_lon : existing.birth_lon,
    city_name: incoming.city_name || existing.city_name,
    gender: incoming.gender || existing.gender,
    timezone: incoming.timezone || existing.timezone,
    sunLongitude: incoming.sunLongitude !== undefined ? incoming.sunLongitude : existing.sunLongitude,
    astrology_cache: incoming.astrology_cache !== undefined ? incoming.astrology_cache : existing.astrology_cache,
    soul_root: incoming.soul_root || existing.soul_root,
    calculatedAt: incoming.calculatedAt || Date.now(),
  };
}

export function sanitizeRecentMaps(rawList: RecentCalculatedMap[]): RecentCalculatedMap[] {
  const result: RecentCalculatedMap[] = [];
  for (const item of rawList) {
    if (!item.firstName || !item.birthDate) continue;
    const existingIndex = result.findIndex((existing) => areMapsEquivalent(existing, item));
    if (existingIndex === -1) {
      result.push(item);
    } else {
      const existing = result[existingIndex];
      const merged = mergeMapEntries(existing, item);
      merged.calculatedAt = Math.max(item.calculatedAt || 0, existing.calculatedAt || 0);
      result[existingIndex] = merged;
    }
  }
  return result.slice(0, MAX_RECENT_MAPS);
}

export function getStoredRecentMaps(): RecentCalculatedMap[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sanitizeRecentMaps(parsed);
  } catch (err) {
    console.error('Failed to parse recent calculated maps from storage:', err);
    return [];
  }
}

export function saveStoredRecentMaps(maps: RecentCalculatedMap[]): void {
  if (typeof window === 'undefined') return;
  try {
    const sanitized = sanitizeRecentMaps(maps);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
    window.dispatchEvent(new CustomEvent(SYNC_EVENT_NAME));
  } catch (err) {
    console.error('Failed to save recent calculated maps to storage:', err);
  }
}

export function getMapDeduplicationKey(map: Partial<RecentCalculatedMap>): string {
  const first = normalizeString(map.firstName);
  const family = normalizeString(map.familyName);
  const date = normalizeDate(map.birthDate);
  if (map.id) return `id_${map.id}_${first}_${family}_${date}`;
  return `name_${first}_${family}_${date}`;
}

export function addRecentCalculatedMap(map: Omit<RecentCalculatedMap, 'calculatedAt'> & { calculatedAt?: number }): RecentCalculatedMap[] {
  const current = getStoredRecentMaps();
  const newItem: RecentCalculatedMap = {
    ...map,
    calculatedAt: map.calculatedAt || Date.now(),
  };

  const existing = current.find((m) => areMapsEquivalent(m, newItem));
  const mergedItem = existing ? mergeMapEntries(existing, newItem) : newItem;

  const filtered = current.filter((m) => !areMapsEquivalent(m, newItem));
  const updated = [mergedItem, ...filtered].slice(0, MAX_RECENT_MAPS);

  saveStoredRecentMaps(updated);
  return updated;
}

export function removeRecentCalculatedMap(keyOrId: string): RecentCalculatedMap[] {
  const current = getStoredRecentMaps();
  const updated = current.filter((m) => {
    if (m.id && (m.id === keyOrId || `id_${m.id}` === keyOrId)) return false;
    if (getMapDeduplicationKey(m) === keyOrId) return false;
    const nameKey = `name_${normalizeString(m.firstName)}_${normalizeString(m.familyName)}_${normalizeDate(m.birthDate)}`;
    if (nameKey === keyOrId) return false;
    return true;
  });

  saveStoredRecentMaps(updated);
  return updated;
}

export function clearRecentCalculatedMaps(): void {
  saveStoredRecentMaps([]);
}

export function useRecentCalculatedMaps() {
  const [recentMaps, setRecentMaps] = useState<RecentCalculatedMap[]>(() => getStoredRecentMaps());

  useEffect(() => {
    const handleSync = () => {
      setRecentMaps(getStoredRecentMaps());
    };

    window.addEventListener(SYNC_EVENT_NAME, handleSync);
    window.addEventListener('storage', handleSync);

    return () => {
      window.removeEventListener(SYNC_EVENT_NAME, handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const addMap = useCallback((map: Omit<RecentCalculatedMap, 'calculatedAt'> & { calculatedAt?: number }) => {
    const updated = addRecentCalculatedMap(map);
    setRecentMaps(updated);
  }, []);

  const removeMap = useCallback((keyOrId: string) => {
    const updated = removeRecentCalculatedMap(keyOrId);
    setRecentMaps(updated);
  }, []);

  const clearAll = useCallback(() => {
    clearRecentCalculatedMaps();
    setRecentMaps([]);
  }, []);

  return {
    recentMaps,
    addMap,
    removeMap,
    clearAll,
  };
}
